// Account/query/session-scoped SWR cache. No tokens or credentials are persisted here.
function createRequestCache(fetcher, { scope = () => '', storage, clock = Date.now, maxEntries = 100 } = {}) {
  const entries = new Map(), inflight = new Map(), listeners = new Map();
  const queue = []; let active = 0;
  const metrics = { requests: 0, hits: 0, shared: 0 };
  const canonical = path => { const u = new URL(path, 'https://local.invalid'); u.searchParams.sort(); return u.pathname + '?' + u.searchParams; };
  const key = path => `rr-analytics-v3:${scope()}:${canonical(path)}`;
  const emit = k => (listeners.get(k) || []).forEach(fn => fn());
  const pinned = k => !!listeners.get(k)?.length || inflight.has(k);
  function trim() {
    // Mounted datasets must not disappear when cover images fill the cache.
    for (const k of entries.keys()) {
      if (entries.size <= maxEntries) break;
      if (!pinned(k)) entries.delete(k);
    }
  }
  function remember(k, value) {
    entries.set(k, value);
    trim();
  }
  function peek(path) {
    const k = key(path); let value = entries.get(k);
    if (!value && storage) { try { value = JSON.parse(storage.getItem(k)); } catch { /* storage unavailable */ } }
    if (!value || clock() - value.at > 12 * 3600000) return null;
    return value;
  }
  const settled = () => { active--; pump(); };
  const pump = () => {
    while (active < 4 && queue.length) {
      const job = queue.shift(); active++;
      Promise.resolve().then(job.fn).then(job.resolve, job.reject).finally(settled);
    }
  };
  const limited = fn => new Promise((resolve,reject) => { queue.push({ fn,resolve,reject }); pump(); });
  async function refresh(path, ttl = 60000) {
    const k = key(path);
    if (inflight.has(k)) { metrics.shared++; return inflight.get(k); }
    const old = peek(path);
    if (old?.retry > clock()) {
      if (old.data) return old.data;
      throw new Error('Analytics are cooling down. Please retry shortly.');
    }
    const work = limited(async () => {
      metrics.requests++;
      try {
        const data = await fetcher(path);
        if (data.error_kind) {
          if (data.error_kind === 'rate_limit') {
            const value = { ...old, data: old?.data || null, at: old?.at || clock(), expires: 0, status: 429,
              error: old?.data ? null : 'Insights are temporarily busy. Retrying shortly.',
              notice: old?.data ? 'Refresh delayed. Showing the last available data.' : null,
              retry: clock() + Math.max(60000, (data.retry_after || 0) * 1000), refreshing: false };
            if (scope() && k === key(path)) remember(k, value);
            emit(k); return value.data;
          }
          const value = { ...old, data: old?.data || data, at: old?.at || clock(), expires: 0,
            error: data.reason || 'Instagram insights are temporarily unavailable.', notice: null, status: null, retry: clock()+60000, refreshing: false };
          if (scope() && k === key(path)) remember(k,value);
          emit(k); return value.data;
        }
        const value = { data, at: clock(), expires: clock() + ttl, refreshing: false, error: null, notice: null, status: null };
        if (scope() && k === key(path)) {
          remember(k, value);
          // Exclude comment text, exports and transient sync status from browser persistence.
          if (storage && data.available !== false && !data.pending && !(data.errors && Object.keys(data.errors).length) && !/comments\/content|sync-status|dms/.test(path)) {
            try {
              storage.setItem(k, JSON.stringify(value));
              const keys = Object.keys(storage).filter(x => x.startsWith('rr-analytics-v3:'));
              let excess = keys.length - maxEntries;
              for (const savedKey of keys) if (excess > 0 && !pinned(savedKey)) { storage.removeItem(savedKey); excess--; }
            } catch { /* quota/private mode: memory cache still works */ }
          }
        }
        emit(k); return data;
      } catch (err) {
        // Permission/session errors must not keep stale data visible.
        if ([401,403,404].includes(err.status)) {
          remember(k, { data: null, at: clock(), expires: 0, error: err.message, retry: clock()+30000 });
          try { storage?.removeItem(k); } catch { /* ignore */ }
        } else if (err.status === 429 || (err.source === 'meta' && err.payload?.code === 'rate_limit')) {
          remember(k, { ...old, at: old?.at || clock(), expires: 0, status: 429,
            error: old?.data ? null : 'Insights are temporarily busy. Retrying shortly.',
            notice: old?.data ? 'Refresh delayed. Showing the last available data.' : null,
            retry: clock() + Math.max(err.source === 'meta' ? 60000 : 30000, (err.retryAfter || 0) * 1000), refreshing: false });
        } else {
          remember(k, { ...old, error: err.message, notice: null, status: null, retry: clock() + Math.max(30000, (err.retryAfter || 0) * 1000), refreshing: false });
        }
        emit(k); throw err;
      }
    }).finally(() => { inflight.delete(k); trim(); emit(k); });
    inflight.set(k, work); emit(k); return work;
  }
  return {
    metrics, peek,
    seed(path, data, ttl = 0) { const k = key(path); remember(k,{data,at:clock(),expires:clock()+ttl,error:null}); emit(k); },
    snapshot(path) { const value = peek(path); return { ...value, refreshing: inflight.has(key(path)) }; },
    subscribe(path, fn) { const k = key(path); const list = listeners.get(k) || []; list.push(fn); listeners.set(k,list); return () => { const next = list.filter(x=>x!==fn); if (next.length) listeners.set(k,next); else listeners.delete(k); trim(); }; },
    async get(path, ttl = 60000, force = false) {
      const value = peek(path);
      if (!force && value?.data) {
        metrics.hits++;
        if (value.expires <= clock() && !(value.retry > clock())) refresh(path,ttl).catch(()=>{});
        return value.data;
      }
      return refresh(path,ttl);
    },
    invalidate(account) {
      const matches = k => k.startsWith(`rr-analytics-v3:${scope()}:`) && new URL(k.slice(k.indexOf(':/analytics')+1), 'https://local.invalid').searchParams.get('account_id') === String(account);
      for (const [k, value] of entries) if (matches(k)) { entries.set(k,{...value,expires:0}); emit(k); }
      if (storage) try { for (const k of Object.keys(storage)) if (matches(k)) { const v = JSON.parse(storage.getItem(k)); storage.setItem(k,JSON.stringify({...v,expires:0})); emit(k); } } catch { /* ignore */ }
    },
  };
}
module.exports = { createRequestCache };
